import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Modal,
  Select,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconEdit,
  IconPlus,
  IconSearch,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import { BeatLoader } from "react-spinners";
import AppHeader from "../../../components/Header/AppHeader";
import DeleteConfirmationModal from "../../../components/DeleteConfirmationModal";
import { apiWrapWithErrorWithData } from "../../../utilities/apiHelpers";
import {
  addBuApi,
  deleteBuApi,
  getBuListAll,
  updateBuApi,
} from "../../../utilities/apis/users";
import colors from "../../../utilities/design";

const normalizeEnabled = (enabled) => {
  if (enabled === true || enabled === 1 || enabled === "1") return true;
  if (enabled === false || enabled === 0 || enabled === "0") return false;
  return Boolean(enabled);
};

const BuList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formName, setFormName] = useState("");
  const [formEnabled, setFormEnabled] = useState("true");
  const [formError, setFormError] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [buToDelete, setBuToDelete] = useState(null);

  const fetchBusinessUnits = async () => {
    setLoading(true);
    const response = await apiWrapWithErrorWithData(getBuListAll());

    if (response?.success) {
      setBusinessUnits(response.result || []);
    } else {
      setBusinessUnits([]);
      showNotification({
        color: "red",
        title: "Business units",
        message: "Could not load business units.",
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchBusinessUnits();
  }, []);

  const filteredBusinessUnits = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return businessUnits.filter((bu) => {
      const matchesSearch =
        !search ||
        String(bu.id).includes(search) ||
        (bu.name || "").toLowerCase().includes(search);

      const enabled = normalizeEnabled(bu.enabled);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "enabled" && enabled) ||
        (statusFilter === "disabled" && !enabled);

      return matchesSearch && matchesStatus;
    });
  }, [businessUnits, searchTerm, statusFilter]);

  const closeFormModal = () => {
    setModalOpen(false);
    setEditId(null);
    setFormName("");
    setFormEnabled("true");
    setFormError("");
  };

  const openCreateModal = () => {
    setEditId(null);
    setFormName("");
    setFormEnabled("true");
    setFormError("");
    setModalOpen(true);
  };

  const openEditModal = (bu) => {
    setEditId(bu.id);
    setFormName(bu.name || "");
    setFormEnabled(normalizeEnabled(bu.enabled) ? "true" : "false");
    setFormError("");
    setModalOpen(true);
  };

  const handleSave = async () => {
    const trimmedName = formName.trim();
    if (!trimmedName) {
      setFormError("Business unit name is required.");
      return;
    }

    setSaving(true);
    setFormError("");

    const response = await apiWrapWithErrorWithData(
      editId
        ? updateBuApi({
            buData: {
              id: editId,
              name: trimmedName,
              enabled: formEnabled === "true",
            },
          })
        : addBuApi({
            buData: {
              name: trimmedName,
              orgId: 1,
            },
          }),
    );

    if (response?.success) {
      showNotification({
        color: "green",
        title: editId ? "Business unit updated" : "Business unit added",
        message: editId
          ? "Business unit was updated successfully."
          : "Business unit was added successfully.",
      });
      closeFormModal();
      fetchBusinessUnits();
    } else {
      showNotification({
        color: "red",
        title: editId ? "Update business unit" : "Add business unit",
        message:
          response?.message ||
          (editId
            ? "Could not update the business unit."
            : "Could not add the business unit."),
      });
    }

    setSaving(false);
  };

  const handleDelete = async () => {
    if (!buToDelete?.id) return;

    setDeleteLoading(true);
    const response = await apiWrapWithErrorWithData(
      deleteBuApi({ id: buToDelete.id }),
    );

    if (response?.success) {
      showNotification({
        color: "green",
        title: "Business unit deleted",
        message:
          response?.message ||
          `"${buToDelete.name}" has been deleted successfully.`,
      });
      setDeleteModalOpen(false);
      setBuToDelete(null);
      fetchBusinessUnits();
    } else {
      showNotification({
        color: "red",
        title: "Delete business unit",
        message: response?.message || "Could not delete the business unit.",
      });
    }

    setDeleteLoading(false);
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:p-4">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <Title order={2}>Business Units</Title>
            <Text size="sm" color="dimmed" mt={4}>
              View, add, edit, and delete business units for your organization.
            </Text>
          </div>
          <Button
            className="rounded-lg bg-[#46BDE1] text-white hover:bg-[#3aa8ca]"
            leftIcon={<IconPlus size={16} />}
            onClick={openCreateModal}
          >
            Add Business Unit
          </Button>
        </div>

        <Card
          shadow="sm"
          radius="md"
          p="md"
          withBorder
          className="mb-6 bg-white"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput
              label="Search"
              placeholder="Search by ID or name..."
              value={searchTerm}
              icon={<IconSearch size={16} className="text-gray-400" />}
              rightSection={
                searchTerm ? (
                  <ActionIcon
                    onClick={() => setSearchTerm("")}
                    size="sm"
                    radius="xl"
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
              onChange={(event) => setSearchTerm(event.currentTarget.value)}
            />
            <Select
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              withinPortal
              data={[
                { value: "all", label: "All statuses" },
                { value: "enabled", label: "Enabled" },
                { value: "disabled", label: "Disabled" },
              ]}
            />
          </div>
        </Card>

        <Card
          shadow="sm"
          radius="md"
          p={0}
          withBorder
          className="overflow-hidden bg-white"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-4 py-20">
              <BeatLoader color={colors.primary} size={12} />
              <Text size="sm" color="dimmed">
                Loading business units...
              </Text>
            </div>
          ) : filteredBusinessUnits.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
              <Text fw={500}>No business units found</Text>
              <Text size="sm" color="dimmed" mt={4}>
                {businessUnits.length === 0
                  ? "Add your first business unit to get started."
                  : "Try adjusting your search or status filter."}
              </Text>
              {businessUnits.length === 0 && (
                <Button
                  className="mt-4 rounded-lg bg-[#46BDE1] text-white hover:bg-[#3aa8ca]"
                  leftIcon={<IconPlus size={16} />}
                  onClick={openCreateModal}
                >
                  Add Business Unit
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table striped highlightOnHover>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBusinessUnits.map((bu) => {
                    const enabled = normalizeEnabled(bu.enabled);

                    return (
                      <tr key={bu.id} className="border-t border-gray-100">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {bu.id}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {bu.name}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            color={enabled ? "green" : "gray"}
                            variant="light"
                          >
                            {enabled ? "Enabled" : "Disabled"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <ActionIcon
                              variant="light"
                              color="blue"
                              onClick={() => openEditModal(bu)}
                              title="Edit business unit"
                            >
                              <IconEdit size={18} />
                            </ActionIcon>
                            <ActionIcon
                              variant="light"
                              color="red"
                              onClick={() => {
                                setBuToDelete(bu);
                                setDeleteModalOpen(true);
                              }}
                              title="Delete business unit"
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      <Modal
        opened={modalOpen}
        onClose={closeFormModal}
        title={editId ? "Edit Business Unit" : "Add Business Unit"}
        centered
      >
        <div className="flex flex-col gap-4">
          <TextInput
            label="Business unit name"
            placeholder="Enter business unit name"
            value={formName}
            error={formError}
            onChange={(event) => {
              setFormName(event.currentTarget.value);
              if (formError) setFormError("");
            }}
          />
          {editId && (
            <Select
              label="Status"
              value={formEnabled}
              withinPortal
              data={[
                { value: "true", label: "Enabled" },
                { value: "false", label: "Disabled" },
              ]}
              onChange={setFormEnabled}
            />
          )}
          <div className="mt-2 flex justify-end gap-3">
            <Button
              variant="default"
              onClick={closeFormModal}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              className="rounded-lg bg-[#46BDE1] text-white hover:bg-[#3aa8ca]"
              loading={saving}
              onClick={handleSave}
            >
              {editId ? "Save Changes" : "Add Business Unit"}
            </Button>
          </div>
        </div>
      </Modal>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        deleteLoading={deleteLoading}
        deleteHandler={handleDelete}
        title="Delete Business Unit"
        text={`Are you sure you want to delete "${buToDelete?.name || "this business unit"}"? This action cannot be undone.`}
      />
    </>
  );
};

export default BuList;
